import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useBoolean } from 'react-use'
import clsx from 'clsx'

import { ProjectsAPI } from '@makyoapp/makyo-sdk'
import { ErrorResponse } from '@makyoapp/makyo-sdk/api/api'
import { ProjectStatus } from '@makyoapp/makyo-sdk/models/Projects'
import { ProjectAdapter } from '@makyoapp/makyo-common'
import { AppCard, AvatarCell, AvatarSize, projectDisplayStatus } from '@makyoapp/makyo-ui'

import { formatError } from '@/lib/error'
import { queryClient } from '@/lib/react-query'
import { queryKeys } from '@/lib/react-query/query-keys'
import { toasted } from '@/lib/toast'
import { useCacheContext } from '@/context/CacheContext'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useReqError } from '@/hooks/useReqError'
import { ORG_ROUTE, ORG_TEAM_ROUTE, PROJECT_ROUTE } from '@/shared/constants'

import ProjectInfo from './ProjectInfo'
import ProjectMenu from './ProjectMenu'
import TeamSelectionModal from './TeamSelectionModal'

interface ProjectItemProps {
  app: ProjectAdapter
  setShowPlanModal: (value: boolean) => void
  setChangePlanApp: (app: ProjectAdapter) => void
  setDelete: (app: ProjectAdapter) => void
  apps: ProjectAdapter[]
}

function ProjectItem({ app, setShowPlanModal, setChangePlanApp, setDelete, apps }: ProjectItemProps) {
  const { t } = useTranslation()

  const { imageCache } = useCacheContext()
  const withCache = !!imageCache.values[app.id]

  // STATES
  const [isFav, setFav] = useBoolean(app.isFavorite)
  const [status, setStatus] = useState<ProjectStatus>(app.status)
  const [isFavLoading, setIsFavLoading] = useBoolean(false)
  const [isStatusLoading, setIsStatusLoading] = useBoolean(false)
  const [isTeamLoading, setIsTeamLoading] = useBoolean(false)
  const [isDuplicateLoading, setIsDuplicateLoading] = useBoolean(false)
  const printAPIError = useReqError()

  const [isTeamModalOpen, setTeamModalOpen] = useState(false)

  const handleChangeStatus = (theStatus: ProjectStatus) => {
    setIsStatusLoading(true)
    ProjectsAPI.ReqChangeStatus(app.id, theStatus)
      .then(() => {
        setStatus(theStatus)
      })
      .catch(printAPIError)
      .finally(() => setIsStatusLoading(false))
  }

  // handle on favorite star button click
  const handleFavClick = () => {
    setIsFavLoading(true)
    ProjectsAPI.ReqFavorite(app.id, !isFav)
      .then(() => {
        setFav(!isFav)
      })
      .catch(printAPIError)
      .finally(() => setIsFavLoading(false))
  }

  const link = app.title.toLowerCase().replaceAll(' ', '-')

  // check if app icon using emoji or uploaded by user
  const isUrl = app?.icon.startsWith('http') || app?.icon.startsWith('blob')

  const handleChangeTeams = () => {
    setTeamModalOpen(true)
  }

  const { currentOrg } = useCurrentOrgContext()
  const orgID = currentOrg?.id

  const handleChangeTeam = (newTeamId: string) => {
    setIsTeamLoading(true)

    ProjectsAPI.ReqTransferAppTeam(app.id, { _id: newTeamId })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: [queryKeys.orgApps, orgID] })
      })
      .catch(printAPIError)
      .finally(() => {
        setIsTeamLoading(false)
        setTeamModalOpen(false)
        toasted(t('Team changed successfully'))
      })
  }

  const handleTransfer = () => {
    // TODO: Implement org transfer modal/logic
    console.log('Transfer clicked')
  }

  const handleDuplicate = () => {
    setIsDuplicateLoading(true)
    let newTitle = `${app.title} Copy`
    let copyNumber = 2
    while (apps.some((existingApp) => existingApp.title?.toLowerCase() === newTitle.toLowerCase())) {
      newTitle = `${app.title} Copy ${copyNumber}`
      copyNumber++
    }

    toast
      .promise(
        ProjectsAPI.ReqDuplicate(app.id, newTitle)
          .then(() => {
            queryClient.invalidateQueries({ queryKey: [queryKeys.orgApps, orgID] })
          })
          .catch((err: string | ErrorResponse | Error) => {
            const { message, status } = formatError(err)

            throw message || t('Failed to duplicate app')
          }),
        {
          pending: t('Duplicating app...'),
          success: t('App duplicated successfully'),
          error: {
            render({ data }) {
              return data || t('Failed to duplicate app')
            },
          },
        }
      )
      .finally(() => {
        setIsDuplicateLoading(false)
      })

    // ProjectsAPI.ReqDuplicate(app.id, `${app.title} Copy`)
    //   .then(() => {
    //     queryClient.invalidateQueries({ queryKey: [queryKeys.orgApps, orgID] })
    //     toasted(t('App duplicated successfully'))
    //   })
    //   .catch(printAPIError)
    //   .finally(() => {
    //     setIsDuplicateLoading(false)
    //   })
  }

  return (
    <>
      <AppCard
        id={`${app.id}`}
        title={
          <Link className='link' to={`${PROJECT_ROUTE}/${app.id}`}>
            {app.title}
          </Link>
        }
        hrefText={`${link}.makyo.me`}
        href={`https://${link}.makyo.me`}
        icon={app.icon}
        withCache={withCache}
        renderIcon={(i) => <Link to={`${PROJECT_ROUTE}/${app.id}`}>{i}</Link>}
        iconClassName={clsx('overflow-hidden border')}
        iconStyle={{
          ...(app.iconBgColor && {
            background: app.iconBgColor,
          }),
          ...(isUrl && {
            padding: 0,
          }),
        }}
        extras={
          <div className='mt-2'>
            <Link className='link' to={`${ORG_ROUTE}/${app.org.id}/${ORG_TEAM_ROUTE}/${app.team.id}`}>
              <AvatarCell size={AvatarSize.XS} {...app.team} />
            </Link>
          </div>
        }
        detailClassName={'text-m-sm'}
        infoTitle={t('App Info')}
        {...(() => {
          const { label, color } = projectDisplayStatus(status, (app as any).publishedAt)
          return { status: t(label), statusColor: color }
        })()}
        isStatusLoading={isStatusLoading}
        // plan={t(app.plan.name)}
        // planColor={app.plan.badgeColor as ColorVariant}
        isFav={isFav}
        onFavClick={handleFavClick}
        isFavLoading={isFavLoading}
        info={
          <ProjectInfo
            plan={app.plan.name}
            status={status}
            meters={{
              records: {
                value: Math.floor(Math.random() * 400),
                max: 500,
                color: 'bg-emerald-400',
              },
              screens: {
                value: Math.floor(Math.random() * 4),
                max: 5,
                color: 'bg-indigo-400',
                singleColor: true,
              },
              usage: {
                value: Math.floor(Math.random() * 100),
                max: 100,
                unit: 'MB',
              },
            }}
            team={<AvatarCell {...app.team} />}
            org={<AvatarCell {...app.org} />}
            creator={<AvatarCell {...app.createdBy} />}
            created={new Date(app.created)}
            updated={new Date(app.updated)}
            href={'#'}
          />
        }
        menuBtnClassName='mk-app-menu-btn'
        menu={
          <ProjectMenu
            id={app.id}
            setStatus={handleChangeStatus}
            setDelete={() => setDelete(app)}
            status={status}
            plan={app.plan.name}
            href={'#'}
            onChangePlan={() => {
              setShowPlanModal(true)
              setChangePlanApp(app)
            }}
            onChangeTeams={handleChangeTeams}
            onTransfer={handleTransfer}
            onDuplicate={handleDuplicate}
          />
        }
      />
      <TeamSelectionModal
        isOpen={isTeamModalOpen}
        onClose={() => setTeamModalOpen(false)}
        onChangeTeam={handleChangeTeam}
        isLoading={isTeamLoading}
        teamId={app.team.id}
      />
    </>
  )
}

export default ProjectItem
